import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Search, Mail, MessageSquare, Phone, Sparkles, Send,
  Clock, User, X, RefreshCw, Inbox, BarChart3, LogOut,
  CheckCircle2, AlertCircle, Trash2, PhoneCall, Calendar, Link2, ChevronLeft,
  Play, Pause, Power
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { agentAPI, gcalAPI } from '../services/api';
import { LOGO_URL } from '../data/mockData';

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

const ActivityIcon = ({ type, direction }) => {
  if (type === 'email' || type === 'ai_draft_email') return <Mail className="w-4 h-4" />;
  if (type === 'sms' || type === 'ai_draft_sms') return <MessageSquare className="w-4 h-4" />;
  if (type === 'call') return <Phone className="w-4 h-4" />;
  if (type === 'chat_handoff') return <Inbox className="w-4 h-4" />;
  return <User className="w-4 h-4" />;
};

// One-click pre-deploy smoke test. Simulates a 5-turn AI voice conversation
// server-side (no Twilio cost, no real lead pinged) and surfaces PASS/FAIL +
// per-turn latency + booking outcome.
const DryRunVoiceButton = () => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await agentAPI.dryRunVoice();
      setResult(r);
    } catch (e) {
      setResult({ overall_pass: false, summary: `Request failed: ${e?.message || e}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="dry-run-voice-btn"
        onClick={run}
        disabled={busy}
        title="Simulate a full 5-turn AI conversation server-side. Pre-deploy smoke test — no real call placed."
        className="text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors"
      >
        <Sparkles className="w-3 h-3" />
        <span className="hidden sm:inline">{busy ? 'Testing AI…' : 'Test AI Call'}</span>
        <span className="sm:hidden">{busy ? '…' : 'Test'}</span>
      </button>
      {result && (
        <div
          data-testid="dry-run-result-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setResult(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-3 rounded-t-xl flex items-center justify-between ${
              result.overall_pass ? 'bg-emerald-50 border-b border-emerald-200' : 'bg-red-50 border-b border-red-200'
            }`}>
              <h3 className="font-bold flex items-center gap-2 text-base">
                {result.overall_pass ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                AI Voice Dry-Run
              </h3>
              <button onClick={() => setResult(null)} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className={`font-semibold ${result.overall_pass ? 'text-emerald-700' : 'text-red-700'}`}>
                {result.summary}
              </div>
              {result.final_state && (
                <div className="text-xs text-gray-600 grid grid-cols-2 gap-2 bg-gray-50 border border-gray-200 rounded p-2">
                  <div>📅 Booking fired: <span className={result.final_state.appointment_booked ? 'text-emerald-700 font-semibold' : 'text-red-700'}>{result.final_state.appointment_booked ? 'YES' : 'NO'}</span></div>
                  <div>↗ Transfer fired: <span className={result.final_state.transferred ? 'text-emerald-700 font-semibold' : 'text-gray-500'}>{result.final_state.transferred ? 'yes' : 'no'}</span></div>
                  {result.final_state.appointment_address && (
                    <div className="col-span-2 text-gray-700">Address parsed: <span className="font-mono">{result.final_state.appointment_address}</span></div>
                  )}
                </div>
              )}
              {result.turns?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Per-turn</div>
                  {result.turns.map((t) => (
                    <div key={t.turn} className={`p-2 rounded border text-xs ${
                      t.pass ? 'border-emerald-200 bg-emerald-50' : 'border-red-300 bg-red-50'
                    }`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{t.pass ? '✓' : '✗'} Turn {t.turn} · {t.label}</span>
                        {t.elapsed_ms != null && (
                          <span className={`text-[10px] ${t.elapsed_ms < 3000 ? 'text-emerald-700' : 'text-orange-700'}`}>
                            {(t.elapsed_ms / 1000).toFixed(2)}s
                          </span>
                        )}
                      </div>
                      <div className="text-gray-600 italic mt-0.5">Lead said: &quot;{t.lead_said}&quot;</div>
                      {t.error && <div className="text-red-700 mt-1 font-mono">{t.error}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// AI master kill-switch shown in the admin top bar. Toggling this PAUSES all
// outbound AI activity (calls, SMS, emails, nurture). The website chat widget
// and lead-capture forms keep working — if a visitor asks for an agent, the
// chat already DMs Alexander instead of warm-transferring.
const AiControlsToggle = ({ onChange }) => {
  const [settings, setSettings] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    agentAPI.getAiSettings()
      .then((s) => {
        if (!active) return;
        setSettings(s);
        onChange?.(s);
      })
      .catch((e) => console.error('Failed to load AI settings', e));
    return () => { active = false; };
  }, [onChange]);

  const toggle = async () => {
    if (!settings || busy) return;
    const next = !settings.outbound_ai_enabled;
    const msg = next
      ? 'Resume AI outbound? Calls, texts, emails and the nurture engine will start firing again.'
      : 'Pause AI outbound?\n\nThis stops ALL outbound AI calls, texts, emails and the 14-day nurture engine.\n\nThe website chat widget keeps working — visitors can still book showings or request to talk to you.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const updated = await agentAPI.updateAiSettings({ outbound_ai_enabled: next });
      setSettings(updated);
      onChange?.(updated);
    } catch (e) {
      alert('Could not update AI settings: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;
  const on = settings.outbound_ai_enabled;
  return (
    <button
      data-testid="ai-master-toggle"
      onClick={toggle}
      disabled={busy}
      title={on
        ? 'AI outbound is ON. Click to PAUSE all calls, texts, emails and nurture.'
        : 'AI outbound is PAUSED. Click to RESUME.'}
      className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors font-semibold ${
        on
          ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300'
          : 'bg-red-100 hover:bg-red-200 text-red-800 border border-red-300'
      } disabled:opacity-60`}
    >
      <Power className={`w-3 h-3 ${on ? '' : 'opacity-70'}`} />
      <span className="hidden sm:inline">{busy ? '…' : (on ? 'AI: ON' : 'AI: PAUSED')}</span>
      <span className="sm:hidden">{on ? 'ON' : 'OFF'}</span>
    </button>
  );
};

// Inline play button for the most recent AI call recording. The backend
// streams the audio through /api/agent/calls/{id}/recording with admin auth
// (token via ?token= query so the HTML <audio> element can authenticate).
const LastCallPlayer = ({ call }) => {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const fmtDur = (s) => {
    const n = parseInt(s || 0, 10);
    if (!n) return '';
    const m = Math.floor(n / 60);
    const ss = n % 60;
    return `${m}:${String(ss).padStart(2, '0')}`;
  };

  const toggle = async (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      return;
    }
    if (!a.src) {
      setLoading(true);
      const token = localStorage.getItem('conti_token');
      const backend = process.env.REACT_APP_BACKEND_URL;
      a.src = `${backend}/api/agent/calls/${call.id}/recording?token=${encodeURIComponent(token)}`;
    }
    try {
      await a.play();
    } catch (err) {
      setLoading(false);
      setPlaying(false);
    }
  };

  return (
    <div
      className="mt-1 inline-flex items-center gap-1.5 text-[11px]"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        data-testid={`play-last-call-${call.id}`}
        onClick={toggle}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded border ${
          playing
            ? 'bg-red-50 border-red-300 text-red-700'
            : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
        } transition-colors`}
        title={playing ? 'Pause AI call recording' : 'Play last AI call recording'}
        aria-label={playing ? 'Pause AI call' : 'Play last AI call'}
      >
        {loading ? (
          <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : playing ? (
          <Pause className="w-3 h-3" />
        ) : (
          <Play className="w-3 h-3" />
        )}
        <span className="font-medium">Last AI call</span>
      </button>
      {call.duration_seconds ? (
        <span className="text-gray-500">{fmtDur(call.duration_seconds)}</span>
      ) : null}
      {call.transferred && (
        <span className="text-emerald-700 font-semibold" title="Realtor was bridged in during this call">↗</span>
      )}
      {call.appointment_event_id && (
        <span className="text-blue-700 font-semibold" title="Appointment booked during this call">📅</span>
      )}
      {call.quality_score && typeof call.quality_score.overall === 'number' && call.quality_score.overall > 0 && (
        <span
          data-testid={`quality-score-${call.id}`}
          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-semibold border ${
            call.quality_score.overall >= 8
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
              : call.quality_score.overall >= 6
              ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
              : 'bg-red-50 border-red-300 text-red-700'
          }`}
          title={`AI conversation quality\nOverall: ${call.quality_score.overall}/10\nListened: ${call.quality_score.listened}/10\nFocused: ${call.quality_score.focused}/10\nOutcome: ${call.quality_score.outcome}\n\n${call.quality_score.summary || ''}`}
        >
          AI {call.quality_score.overall}/10
        </span>
      )}
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => { setPlaying(true); setLoading(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => { setPlaying(false); setLoading(false); }}
        onLoadStart={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
      />
    </div>
  );
};

const LeadRow = ({ lead, selected, onSelect, checked, onToggleCheck }) => {
  const name = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email;
  const last = lead.last_activity;
  const lastWhen = last?.created_at || lead.last_activity_at || lead.created_at;
  return (
    <div
      className={`w-full p-3 border-b border-gray-200 hover:bg-red-50 transition relative flex items-start gap-2 ${
        selected ? 'bg-red-50 border-l-4 border-l-red-600' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onToggleCheck?.(lead.id, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 flex-shrink-0 cursor-pointer"
        data-testid={`lead-checkbox-${lead.id}`}
        aria-label={`Select ${name}`}
      />
      <div
        data-testid={`lead-row-${lead.id}`}
        onClick={() => onSelect(lead)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(lead); } }}
        role="button"
        tabIndex={0}
        className="flex-1 text-left min-w-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-gray-900 truncate">{name}</div>
            <div className="text-xs text-gray-500 truncate">{lead.email}</div>
            <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(lastWhen)}
              {lead.kvcore_synced && (
                <span className="ml-1 text-green-600 inline-flex items-center" title="Synced to KVcore">
                  <CheckCircle2 className="w-3 h-3" />
                </span>
              )}
            </div>
          </div>
          {lead.has_pending_draft && (
            <span className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">
              DRAFT
            </span>
          )}
        </div>
        {lead.preferredArea && (
          <div className="text-xs text-gray-600 mt-1 truncate">📍 {lead.preferredArea}</div>
        )}
        {lead.last_call && (
          <LastCallPlayer call={lead.last_call} />
        )}
      </div>
    </div>
  );
};

const ActivityItem = ({ act, onCancel, onSend }) => {
  const meta = act.metadata || {};
  const isDraft = (act.type === 'ai_draft_email' || act.type === 'ai_draft_sms') && !meta.sent;
  const isCancelled = meta.cancelled;
  const isSent = meta.sent;
  const autoSent = meta.auto_sent;

  let bg = 'bg-white';
  let border = 'border-gray-200';
  if (isDraft) { bg = 'bg-yellow-50'; border = 'border-yellow-300'; }
  if (isCancelled) { bg = 'bg-gray-50'; border = 'border-gray-200'; }
  if (autoSent) { bg = 'bg-blue-50'; border = 'border-blue-200'; }

  return (
    <div className={`border ${border} ${bg} rounded-lg p-2 sm:p-3 text-sm`}>
      <div className="flex flex-wrap items-center justify-between gap-1 mb-2">
        <div className="flex items-center gap-1.5 text-gray-700 font-medium text-[10px] sm:text-xs uppercase tracking-wide min-w-0">
          <ActivityIcon type={act.type} direction={act.direction} />
          <span className="truncate">
            {act.type.replace('ai_draft_', 'AI Draft · ').replace('_', ' ')}
            {act.direction === 'in' && ' · received'}
            {act.direction === 'out' && ' · sent'}
            {autoSent && ' · auto-sent'}
            {isCancelled && ' · cancelled'}
          </span>
        </div>
        <span className="text-[10px] sm:text-xs text-gray-400 flex-shrink-0">{formatTime(act.created_at)}</span>
      </div>
      <div className="whitespace-pre-wrap break-words text-gray-800 text-xs sm:text-sm">{act.body}</div>
      {isDraft && (
        <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
          <span className="text-yellow-700 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Auto-sends at {meta.autosend_at ? new Date(meta.autosend_at).toLocaleTimeString() : 'soon'}
          </span>
          <button
            data-testid={`send-draft-${act.id}`}
            onClick={() => onSend(act)}
            className="ml-auto bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
          >
            <Send className="w-3 h-3" /> Send now
          </button>
          <button
            data-testid={`cancel-draft-${act.id}`}
            onClick={() => onCancel(act)}
            className="text-gray-600 hover:text-red-600 px-2 py-1 rounded text-xs flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const LeadDetail = ({ lead, onUpdate, gcalConnected }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composer, setComposer] = useState({ channel: 'email', subject: '', body: '' });
  const [instruction, setInstruction] = useState('');
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [draftingSms, setDraftingSms] = useState(false);
  const [sending, setSending] = useState(false);
  const [callScript, setCallScript] = useState('');
  const [showScript, setShowScript] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callingNow, setCallingNow] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [nurture, setNurture] = useState(null);
  const [nurtureBusy, setNurtureBusy] = useState(false);
  const [alertsBusy, setAlertsBusy] = useState(false);
  // Default to true (opt-in) when the field isn't set yet — matches the alert
  // service which treats "missing" as enabled.
  const alertsEnabled = lead?.listing_alerts_enabled !== false;

  const refresh = async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const [data, nurtData] = await Promise.all([
        agentAPI.getLead(lead.id),
        agentAPI.getNurture(lead.id).catch(() => ({ campaign: null })),
      ]);
      setActivities(data.activities || []);
      setNurture(nurtData?.campaign || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!lead?.id) return undefined;
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const [data, nurtData] = await Promise.all([
          agentAPI.getLead(lead.id),
          agentAPI.getNurture(lead.id).catch(() => ({ campaign: null })),
        ]);
        if (!active) return;
        setActivities(data.activities || []);
        setNurture(nurtData?.campaign || null);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [lead?.id]);

  const isNurtureActive = nurture && nurture.status === 'active';

  const toggleNurture = async () => {
    if (nurtureBusy) return;
    setNurtureBusy(true);
    try {
      if (isNurtureActive) {
        if (!confirm(`Stop the 14-day AI Speed-to-Lead nurture for ${fullName}?\n\nThis will halt all automated SMS, emails, and calls.`)) {
          setNurtureBusy(false);
          return;
        }
        await agentAPI.stopNurture(lead.id);
      } else {
        if (!confirm(`Start the 14-day AI Speed-to-Lead nurture for ${fullName}?\n\nLauren will automatically text, email, and call over the next 14 days until they engage.`)) {
          setNurtureBusy(false);
          return;
        }
        await agentAPI.startNurture(lead.id);
      }
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setNurtureBusy(false);
    }
  };

  const toggleListingAlerts = async () => {
    if (alertsBusy) return;
    const next = !alertsEnabled;
    const msg = next
      ? `Resume listing alerts for ${fullName}?\n\nThey'll start receiving email notifications whenever a new property matches their saved search.`
      : `Pause listing alerts for ${fullName}?\n\nThey'll stop receiving new-listing emails even if they have active saved searches on their account.\n\n(Flip this back on anytime to resume.)`;
    if (!confirm(msg)) return;
    setAlertsBusy(true);
    try {
      await agentAPI.setListingAlerts(lead.id, next);
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAlertsBusy(false);
    }
  };

  const draftEmail = async () => {
    setDraftingEmail(true);
    try {
      const res = await agentAPI.draftEmail(lead.id, instruction);
      setComposer({ channel: 'email', subject: res.draft.subject, body: res.draft.body });
      setInstruction('');
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('AI draft failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDraftingEmail(false);
    }
  };

  const draftSms = async () => {
    setDraftingSms(true);
    try {
      const res = await agentAPI.draftSms(lead.id, instruction);
      setComposer({ channel: 'sms', subject: '', body: res.draft.body });
      setInstruction('');
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('AI draft failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDraftingSms(false);
    }
  };

  const generateScript = async () => {
    try {
      const res = await agentAPI.callScript(lead.id);
      setCallScript(res.script);
      setShowScript(true);
    } catch (e) {
      alert('Failed to generate script: ' + e.message);
    }
  };

  const startAICall = async () => {
    if (!window.confirm(`Start AI call to ${lead.firstName || 'lead'} at ${lead.phone}?`)) return;
    setCallingNow(true);
    try {
      const res = await agentAPI.startCall(lead.id);
      setActiveCall({ id: res.call_id, status: res.status, transferred: false });
      await refresh();
      // Poll for call updates
      const poll = setInterval(async () => {
        try {
          const c = await agentAPI.getCall(res.call_id);
          setActiveCall({ id: c.id, status: c.status, transferred: c.transferred,
                          conversation: c.conversation, duration: c.duration_seconds });
          if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(c.status)) {
            clearInterval(poll);
            await refresh();
          }
        } catch { /* ignore */ }
      }, 4000);
      // Auto-stop polling after 10 min
      setTimeout(() => clearInterval(poll), 10 * 60 * 1000);
    } catch (e) {
      alert('Call failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCallingNow(false);
    }
  };

  const warmTransfer = async () => {
    if (!activeCall) return;
    if (!window.confirm('Dial your phone (+12269344107) into the live call now?')) return;
    try {
      await agentAPI.warmTransfer(activeCall.id);
      setActiveCall((c) => c ? { ...c, transferred: true } : c);
      await refresh();
    } catch (e) {
      alert('Warm transfer failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sendNow = async (channelOverride, subjectOverride, bodyOverride) => {
    const channel = channelOverride || composer.channel;
    const subject = subjectOverride ?? composer.subject;
    const body = bodyOverride ?? composer.body;
    if (!body.trim()) return;
    setSending(true);
    try {
      if (channel === 'email') {
        await agentAPI.sendEmail(lead.id, subject || 'Following up from Alexander Conti', body);
      } else {
        await agentAPI.sendSms(lead.id, body);
      }
      setComposer({ channel, subject: '', body: '' });
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('Send failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const sendDraftNow = async (act) => {
    const meta = act.metadata || {};
    if (act.type === 'ai_draft_email') {
      await sendNow('email', meta.subject, meta.body);
    } else if (act.type === 'ai_draft_sms') {
      await sendNow('sms', '', meta.body);
    }
  };

  const cancelDraft = async (act) => {
    if (!window.confirm('Cancel this AI draft? It will not be auto-sent.')) return;
    try {
      await agentAPI.cancelDraft(act.id);
      await refresh();
      onUpdate?.();
    } catch (e) {
      alert('Cancel failed: ' + e.message);
    }
  };

  const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 p-3 sm:p-4 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate" data-testid="lead-detail-name">{fullName}</h2>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs sm:text-sm text-gray-600">
              {lead.email && <span className="flex items-center gap-1 break-all"><Mail className="w-3 h-3 flex-shrink-0" /> {lead.email}</span>}
              {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" /> {lead.phone}</span>}
              {lead.preferredArea && <span>📍 {lead.preferredArea}</span>}
              {lead.priceRange && <span>💰 {lead.priceRange}</span>}
            </div>
            {lead.message && (
              <div className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 max-w-2xl">
                <span className="font-semibold">Original message:</span> {lead.message}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <button
              onClick={toggleNurture}
              disabled={nurtureBusy}
              className={`text-xs px-2 py-1 rounded border ${
                isNurtureActive
                  ? 'text-red-600 hover:text-red-800 border-red-200 hover:border-red-400'
                  : 'text-emerald-700 hover:text-emerald-900 border-emerald-200 hover:border-emerald-400'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="toggle-nurture-btn"
              title={isNurtureActive
                ? 'Stop the 14-day AI Speed-to-Lead nurture'
                : 'Start the 14-day AI Speed-to-Lead nurture'}
            >
              {nurtureBusy ? '…' : (isNurtureActive ? 'Stop AI Nurture' : 'Start AI Nurture')}
            </button>
            <button
              onClick={toggleListingAlerts}
              disabled={alertsBusy}
              className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${
                alertsEnabled
                  ? 'text-emerald-700 hover:text-emerald-900 border-emerald-200 hover:border-emerald-400'
                  : 'text-gray-500 hover:text-gray-800 border-gray-200 hover:border-gray-400 bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="toggle-listing-alerts"
              title={alertsEnabled
                ? 'Listing alerts are ON for this contact — click to PAUSE'
                : 'Listing alerts are PAUSED for this contact — click to RESUME'}
            >
              <Inbox className="w-3 h-3" />
              {alertsBusy ? '…' : (alertsEnabled ? 'Alerts: ON' : 'Alerts: OFF')}
            </button>
            <button
              onClick={async () => {
                if (!confirm(`⚠️ Permanently DELETE ${fullName} from the admin?\n\n• Removes the lead and all activities, calls, drafts, and nurture history.\n• KVcore record is NOT touched (delete it separately if needed).\n\nThis cannot be undone.`)) return;
                try {
                  await agentAPI.deleteLead(lead.id);
                  onUpdate?.(null); // clear selection + refresh list
                } catch (e) {
                  alert('Delete failed: ' + (e.response?.data?.detail || e.message));
                }
              }}
              className="text-xs text-gray-500 hover:text-red-700 border border-gray-200 hover:border-red-400 px-2 py-1 rounded"
              data-testid="delete-lead-btn"
              title="Permanently delete this lead from the admin"
            >
              Delete
            </button>
            <button onClick={refresh} className="text-gray-400 hover:text-gray-700" data-testid="refresh-lead">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Activity timeline */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 bg-gray-50" data-testid="activity-timeline">
        {activities.length === 0 && !loading && (
          <div className="text-center text-gray-400 mt-12 text-sm">
            <Inbox className="w-12 h-12 mx-auto mb-2 opacity-40" />
            No activity yet. Use the AI tools below to start the conversation.
          </div>
        )}
        {activities.map((act) => (
          <ActivityItem key={act.id} act={act} onCancel={cancelDraft} onSend={sendDraftNow} />
        ))}
      </div>

      {/* AI Toolbar */}
      <div className="border-t border-gray-200 bg-white p-2 sm:p-3 space-y-2">
        <input
          data-testid="ai-instruction"
          type="text"
          placeholder="Optional AI steer (e.g., 'mention 24 Oak St')"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 items-center">
          <button
            data-testid="ai-draft-email-btn"
            onClick={draftEmail}
            disabled={draftingEmail}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:opacity-60 text-white text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-lg flex items-center justify-center gap-1"
          >
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{draftingEmail ? 'Drafting…' : 'AI Draft Email'}</span>
          </button>
          <button
            data-testid="ai-draft-sms-btn"
            onClick={draftSms}
            disabled={draftingSms || !lead.phone}
            title={!lead.phone ? 'Lead has no phone' : ''}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:opacity-40 text-white text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-lg flex items-center justify-center gap-1"
          >
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{draftingSms ? 'Drafting…' : 'AI Draft SMS'}</span>
          </button>
          <button
            data-testid="ai-call-script-btn"
            onClick={generateScript}
            disabled={!lead.phone}
            className="bg-gray-700 hover:bg-gray-800 disabled:opacity-40 text-white text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-lg flex items-center justify-center gap-1"
          >
            <PhoneCall className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Call Script</span>
          </button>
          <button
            data-testid="ai-call-btn"
            onClick={startAICall}
            disabled={!lead.phone || callingNow || !!activeCall}
            title={!lead.phone ? 'Lead has no phone' : ''}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-lg flex items-center justify-center gap-1"
          >
            <PhoneCall className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{callingNow ? 'Dialling…' : 'AI Call Lead'}</span>
          </button>
          <button
            data-testid="schedule-btn"
            onClick={() => setShowSchedule(true)}
            disabled={!gcalConnected}
            title={!gcalConnected ? 'Connect Google Calendar first' : ''}
            className="col-span-2 sm:col-span-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-lg flex items-center justify-center gap-1"
          >
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Schedule</span>
          </button>
        </div>

        {/* Active call panel */}
        {activeCall && (
          <div data-testid="active-call-panel" className="border border-green-300 bg-green-50 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold flex items-center gap-2 text-green-800">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live AI call · {activeCall.status}
                {activeCall.transferred && ' · realtor bridged'}
              </div>
              <div className="flex gap-2">
                {!activeCall.transferred && (
                  <button
                    data-testid="warm-transfer-btn"
                    onClick={warmTransfer}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded flex items-center gap-1"
                  >
                    <Phone className="w-3 h-3" /> Merge me in
                  </button>
                )}
                <button
                  onClick={() => setActiveCall(null)}
                  className="text-gray-500 hover:text-gray-700 text-xs"
                >
                  Hide
                </button>
              </div>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
              {(activeCall.conversation || []).slice(-6).map((m, i) => (
                <div key={i} className={m.role === 'user' ? 'text-gray-800' : 'text-green-900'}>
                  <span className="font-semibold">{m.role === 'user' ? 'Lead' : 'AI'}:</span> {m.content}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Composer */}
        {composer.body && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="composer">
            <div className="flex items-center gap-2 mb-2">
              <select
                value={composer.channel}
                onChange={(e) => setComposer({ ...composer, channel: e.target.value })}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
              {composer.channel === 'email' && (
                <input
                  data-testid="composer-subject"
                  type="text"
                  placeholder="Subject"
                  value={composer.subject}
                  onChange={(e) => setComposer({ ...composer, subject: e.target.value })}
                  className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                />
              )}
              <button
                onClick={() => setComposer({ channel: 'email', subject: '', body: '' })}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              data-testid="composer-body"
              rows={composer.channel === 'sms' ? 3 : 8}
              value={composer.body}
              onChange={(e) => setComposer({ ...composer, body: e.target.value })}
              className="w-full text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">
                {composer.channel === 'sms' && `${composer.body.length} chars`}
              </span>
              <button
                data-testid="composer-send"
                onClick={() => sendNow()}
                disabled={sending || !composer.body.trim()}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg flex items-center gap-1"
              >
                <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send Now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Call script modal */}
      {showScript && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Call Script for {fullName}</h3>
              <button onClick={() => setShowScript(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto whitespace-pre-wrap text-sm text-gray-800">
              {callScript}
            </div>
            <div className="p-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => { navigator.clipboard.writeText(callScript); }}
                className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                Copy
              </button>
              <a
                href={`tel:${lead.phone}`}
                className="text-sm px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1"
              >
                <PhoneCall className="w-3 h-3" /> Call {lead.phone}
              </a>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <ScheduleModal
          lead={lead}
          onClose={() => setShowSchedule(false)}
          onSuccess={() => { setShowSchedule(false); refresh(); }}
        />
      )}
    </div>
  );
};

// Schedule appointment modal
const ScheduleModal = ({ lead, onClose, onSuccess }) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);
  const [time, setTime] = useState('15:00');
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState('Phone call / Zoom');
  const [summary, setSummary] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onBook = async () => {
    if (!date || !time) { setError('Pick a date and time'); return; }
    setError(''); setSaving(true);
    try {
      const start = new Date(`${date}T${time}:00`);
      const end = new Date(start.getTime() + duration * 60 * 1000);
      // Build ISO strings WITH local offset
      const tzOffset = -start.getTimezoneOffset();
      const sign = tzOffset >= 0 ? '+' : '-';
      const pad = (n) => String(Math.abs(n)).padStart(2, '0');
      const offset = `${sign}${pad(Math.floor(Math.abs(tzOffset) / 60))}:${pad(Math.abs(tzOffset) % 60)}`;
      const toIso = (d) => {
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${offset}`;
      };
      await gcalAPI.createEvent({
        lead_id: lead.id,
        summary: summary || undefined,
        start_iso: toIso(start),
        end_iso: toIso(end),
        location: location || undefined,
        send_invite_to_lead: sendInvite,
      });
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold flex items-center gap-2"><Calendar className="w-4 h-4" /> Book appointment</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {error && <div className="bg-red-50 text-red-700 text-sm p-2 rounded mb-3">{error}</div>}
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input
              data-testid="schedule-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={`Showing / Consultation — ${lead.firstName || 'Lead'}`}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input
                data-testid="schedule-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
              <input
                data-testid="schedule-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Duration (min)</label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
            >
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={45}>45</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location / link</label>
            <input
              data-testid="schedule-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="46 Charing Cross, Brantford / Zoom link"
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={sendInvite}
              onChange={(e) => setSendInvite(e.target.checked)}
            />
            Email calendar invite to {lead.email || 'lead'}
          </label>
        </div>
        <button
          data-testid="schedule-confirm"
          onClick={onBook}
          disabled={saving}
          className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
        >
          {saving ? 'Booking…' : 'Book on Google Calendar'}
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [gcal, setGcal] = useState({ connected: false, email: null });
  const [aiSettings, setAiSettings] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  const allChecked = leads.length > 0 && checkedIds.size === leads.length;
  const someChecked = checkedIds.size > 0;

  const toggleCheck = (leadId, checked) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(leadId); else next.delete(leadId);
      return next;
    });
  };
  const toggleCheckAll = () => {
    if (allChecked) setCheckedIds(new Set());
    else setCheckedIds(new Set(leads.map((l) => l.id)));
  };
  const bulkDelete = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    if (!confirm(`⚠️ Permanently DELETE ${ids.length} lead${ids.length > 1 ? 's' : ''}?\n\nThis removes all activities, calls, drafts, and nurture history for each. KVcore records are NOT touched. Cannot be undone.`)) return;
    try {
      const res = await agentAPI.bulkDeleteLeads(ids);
      setCheckedIds(new Set());
      if (selected && ids.includes(selected.id)) setSelected(null);
      await fetchAll();
      alert(`Deleted ${res.deleted.leads} lead${res.deleted.leads !== 1 ? 's' : ''}.`);
    } catch (e) {
      alert('Bulk delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsData, leadsData, gcalStatus] = await Promise.all([
        agentAPI.getStats(),
        agentAPI.listLeads(q),
        gcalAPI.getStatus().catch(() => ({ connected: false })),
      ]);
      setStats(statsData);
      setLeads(leadsData.leads || []);
      setGcal(gcalStatus);
      if (!selected && leadsData.leads?.length > 0) {
        setSelected(leadsData.leads[0]);
      } else if (selected) {
        // Keep the currently-selected lead in sync with its fresh server copy
        // so toggles (listing_alerts_enabled, kvcore_synced, etc.) reflect
        // immediately in LeadDetail without a manual re-select.
        const fresh = (leadsData.leads || []).find((l) => l.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      console.error('Failed to fetch:', e);
    } finally {
      setRefreshing(false);
    }
  }, [q, selected]);

  const connectGoogleCalendar = async () => {
    try {
      const res = await gcalAPI.getAuthUrl();
      window.location.href = res.url;
    } catch (e) {
      alert('Could not start Google sign-in: ' + (e.response?.data?.detail || e.message));
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    await gcalAPI.disconnect();
    setGcal({ connected: false });
  };

  useEffect(() => {
    if (user && user.is_admin) {
      fetchAll();
      const interval = setInterval(fetchAll, 30000); // poll every 30s
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!user.is_admin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
        <h2 className="text-xl font-bold mb-2">Admin access required</h2>
        <p className="text-gray-600 mb-4">Your account is signed in but doesn&apos;t have admin privileges.</p>
        <button onClick={() => navigate('/')} className="bg-red-600 text-white px-4 py-2 rounded-lg">
          Back to site
        </button>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-100">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {/* Mobile-only back button when a lead is selected */}
          {selected && (
            <button
              data-testid="mobile-back-to-leads"
              onClick={() => setSelected(null)}
              className="md:hidden -ml-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-700"
              aria-label="Back to leads"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-base sm:text-lg font-bold flex items-center gap-2 min-w-0">
            <span className="relative h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 rounded-full overflow-hidden bg-black">
              <img src={LOGO_URL} alt="AC" className="absolute inset-0 h-full w-full object-cover scale-[1.6]" />
            </span>
            <span className="truncate">AI Sales Agent</span>
          </h1>
          {stats && (
            <div className="hidden md:flex gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1" data-testid="stats-total-leads"><BarChart3 className="w-3 h-3" /> {stats.total_leads} leads</span>
              <span className="flex items-center gap-1" data-testid="stats-recent-leads"><Inbox className="w-3 h-3" /> {stats.recent_leads_7d} this week</span>
              <span className="flex items-center gap-1 text-yellow-700" data-testid="stats-pending-drafts">
                <Clock className="w-3 h-3" /> {stats.pending_drafts} pending drafts (auto-send {stats.autosend_delay_minutes}m)
              </span>
              <span
                className="flex items-center gap-1 text-emerald-700"
                data-testid="stats-ai-transfer-rate"
                title={`${stats.ai_calls_transferred || 0} warm transfers of ${stats.ai_calls_completed || 0} completed AI calls`}
              >
                <Sparkles className="w-3 h-3" /> AI transfer rate: {stats.ai_transfer_rate_pct ?? 0}%
              </span>
              <span
                className="flex items-center gap-1 text-blue-700"
                data-testid="stats-ai-appt-rate"
                title={`${stats.ai_appointments_booked || 0} appointments booked across ${stats.ai_calls_completed || 0} completed AI calls`}
              >
                <Calendar className="w-3 h-3" /> AI appt rate: {stats.ai_appointment_rate_pct ?? 0}%
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {gcal.connected ? (
            <button
              data-testid="gcal-status"
              onClick={disconnectGoogleCalendar}
              className="text-xs text-green-700 hover:text-red-600 flex items-center gap-1"
              title={`Connected as ${gcal.email || 'Google'}`}
            >
              <Calendar className="w-3 h-3" />
              <span className="hidden sm:inline">GCal: {gcal.email || 'connected'}</span>
              <span className="sm:hidden">GCal</span>
            </button>
          ) : (
            <button
              data-testid="gcal-connect"
              onClick={connectGoogleCalendar}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded flex items-center gap-1"
            >
              <Link2 className="w-3 h-3" />
              <span className="hidden sm:inline">Connect Google Calendar</span>
              <span className="sm:hidden">Connect GCal</span>
            </button>
          )}
          <span className="text-sm text-gray-600 hidden md:inline">{user.email}</span>
          <AiControlsToggle onChange={setAiSettings} />
          <DryRunVoiceButton />
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate('/'); }}
            className="text-sm text-gray-600 hover:text-red-600 flex items-center gap-1"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>

      {/* AI paused banner — red strip across the top so it's impossible to miss */}
      {aiSettings && !aiSettings.outbound_ai_enabled && (
        <div
          data-testid="ai-paused-banner"
          className="bg-red-600 text-white px-3 sm:px-4 py-2 text-xs sm:text-sm flex items-center justify-center gap-2 font-semibold"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            AI outbound is <span className="underline">PAUSED</span>. No calls, texts, emails or
            nurture will be sent. Website chat keeps working.
          </span>
        </div>
      )}

      {/* Mobile-only stats strip */}
      {stats && (
        <div className="md:hidden flex gap-2 overflow-x-auto px-3 py-2 bg-white border-b border-gray-200 text-[10px] text-gray-600 whitespace-nowrap">
          <span className="flex items-center gap-1 flex-shrink-0"><BarChart3 className="w-3 h-3" /> {stats.total_leads} leads</span>
          <span className="flex items-center gap-1 flex-shrink-0"><Inbox className="w-3 h-3" /> {stats.recent_leads_7d}/week</span>
          <span className="flex items-center gap-1 flex-shrink-0 text-yellow-700"><Clock className="w-3 h-3" /> {stats.pending_drafts} drafts</span>
          <span className="flex items-center gap-1 flex-shrink-0 text-emerald-700"><Sparkles className="w-3 h-3" /> {stats.ai_transfer_rate_pct ?? 0}% transfer</span>
          <span className="flex items-center gap-1 flex-shrink-0 text-blue-700"><Calendar className="w-3 h-3" /> {stats.ai_appointment_rate_pct ?? 0}% appt</span>
        </div>
      )}

      {/* Main split layout — stacks on mobile (show list OR detail), side-by-side on md+ */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Leads list — full width on mobile when no lead selected, hidden on mobile when lead selected */}
        <div className={`${selected ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 md:border-r border-gray-200 bg-white flex-col`}>
          <div className="p-3 border-b border-gray-200 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="leads-search"
                type="text"
                placeholder="Search leads…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchAll()}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <button
              data-testid="add-lead-btn"
              onClick={() => setShowAddLead(true)}
              className="w-full text-sm bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-2 rounded-lg flex items-center justify-center gap-1"
            >
              + Add Lead (KVcore / Manual)
            </button>
          </div>
          {/* Bulk-select toolbar */}
          {leads.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50 text-xs">
              <label className="flex items-center gap-2 cursor-pointer text-gray-700">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                  onChange={toggleCheckAll}
                  data-testid="select-all-leads"
                  className="cursor-pointer"
                />
                {someChecked ? `${checkedIds.size} selected` : 'Select all'}
              </label>
              {someChecked && (
                <button
                  onClick={bulkDelete}
                  data-testid="bulk-delete-btn"
                  className="text-red-600 hover:text-red-800 font-medium"
                >
                  Delete selected
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto" data-testid="leads-list">
            {leads.length === 0 && !refreshing && (
              <div className="p-6 text-center text-gray-400 text-sm">No leads yet.</div>
            )}
            {leads.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                selected={selected?.id === lead.id}
                onSelect={setSelected}
                checked={checkedIds.has(lead.id)}
                onToggleCheck={toggleCheck}
              />
            ))}
          </div>
        </div>

        {/* Right: Detail — hidden on mobile when no lead selected, full width on mobile when selected */}
        <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-1 overflow-hidden`}>
          {selected ? (
            <LeadDetail
              key={selected.id}
              lead={selected}
              onUpdate={(action) => {
                if (action === null) { setSelected(null); }
                fetchAll();
              }}
              gcalConnected={gcal.connected}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">
              Select a lead to view their conversation timeline.
            </div>
          )}
        </div>
      </div>

      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onCreated={(lead) => {
            setShowAddLead(false);
            fetchAll();
            setSelected(lead);
          }}
        />
      )}
    </div>
  );
};

// ---- Add Lead Modal ----
const AddLeadModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    interestedIn: 'buying', preferredArea: '', priceRange: '',
    message: '', source: 'KVcore import',
    start_nurture: true, push_to_kvcore: false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.firstName.trim()) { alert('First name is required.'); return; }
    if (!form.email.trim() && !form.phone.trim()) { alert('Email or phone is required.'); return; }
    setSaving(true);
    try {
      const res = await agentAPI.createLead(form);
      onCreated?.(res.lead);
    } catch (e) {
      alert('Could not add lead: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-lead-modal"
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Add Lead</h3>
          <p className="text-xs text-gray-500 mt-1">
            Use this for leads that came into KVcore from external sources (Facebook ads, manual KVcore entry, etc.) so the AI can nurture them too.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="add-lead-first" placeholder="First name *" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
            <input data-testid="add-lead-last" placeholder="Last name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
          </div>
          <input data-testid="add-lead-email" placeholder="Email *" value={form.email} onChange={(e) => set('email', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <input data-testid="add-lead-phone" placeholder="Phone (e.g. +12269991234) *" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select data-testid="add-lead-interested" value={form.interestedIn} onChange={(e) => set('interestedIn', e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="buying">Buying</option>
              <option value="selling">Selling</option>
              <option value="both">Both</option>
              <option value="investing">Investing</option>
              <option value="newbuild">New Build</option>
            </select>
            <select data-testid="add-lead-price" value={form.priceRange} onChange={(e) => set('priceRange', e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">— Price range —</option>
              <option value="under-500">Under $500K</option>
              <option value="500-750">$500K - $750K</option>
              <option value="750-1m">$750K - $1M</option>
              <option value="1m-1.5m">$1M - $1.5M</option>
              <option value="over-1.5m">$1.5M+</option>
            </select>
          </div>
          <input data-testid="add-lead-area" placeholder="Preferred area (e.g. West Brantford, Ancaster)" value={form.preferredArea} onChange={(e) => set('preferredArea', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <input data-testid="add-lead-source" placeholder="Source (e.g. KVcore - Facebook Ad)" value={form.source} onChange={(e) => set('source', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <textarea data-testid="add-lead-message" placeholder="Notes / message" value={form.message} onChange={(e) => set('message', e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="add-lead-start-nurture" type="checkbox" checked={form.start_nurture} onChange={(e) => set('start_nurture', e.target.checked)} />
            Start the 14-day AI Speed-to-Lead nurture for this lead
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="add-lead-push-kvcore" type="checkbox" checked={form.push_to_kvcore} onChange={(e) => set('push_to_kvcore', e.target.checked)} />
            Also push to KVcore (only check if NOT already in KVcore — avoids duplicate)
          </label>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900" data-testid="add-lead-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-60 text-white rounded-lg" data-testid="add-lead-save">
            {saving ? 'Saving…' : 'Add Lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
